/**
 * Smart Playlist Service — Ella's Living Playlists
 *
 * API layer for AI-generated, adaptive, self-scoring playlists.
 */

import { api } from './api';

// ── Types ────────────────────────────────────────────────────────────

export interface SmartPlaylistTrack {
  trackId: string;
  source: string;
  title: string;
  artistName: string;
  albumTitle: string | null;
  albumId: string | null;
  coverUrl: string | null;
  duration: number;
  segmentId: string;
  position: number;
  energyEstimate: number;
  familiarityToUser: string;
}

export interface SmartPlaylistSegment {
  id: string;
  mood: string;
  startMinute: number;
  endMinute: number;
  trackCount: number;
}

export interface CompactPoolTrack {
  id: string | null;
  title: string;
  artist: string;
  year: number | null;
  src: string;
  pop: number | null;
  energy: number | null;
}

export interface GenerateResult {
  success: boolean;
  playlistId: string;
  name: string;
  description: string;
  ellaResponse: string;
  trackCount: number;
  totalDurationSeconds: number;
  segments: SmartPlaylistSegment[];
  tracks: SmartPlaylistTrack[];
  poolSlice?: CompactPoolTrack[];
  singleTrack?: boolean;
  twoStage?: boolean;
  status?: string;
  buildStatus?: string;
  targetTrackCount?: number;
  interpretedSeed?: {
    seedType?: string;
    seedValue?: string;
    tuningParams?: GenerateOptions['tuningParams'];
  };
  track?: {
    trackId: string;
    source: string;
    title: string;
    artistName: string;
    albumTitle: string;
    albumId: string;
    coverUrl: string;
    duration: number;
  };
}

export interface Stage1GenerateResult extends GenerateResult {
  twoStage: true;
  targetTrackCount: number;
  opener?: SmartPlaylistTrack;
}

export interface ModifyResult {
  success: boolean;
  ellaResponse: string;
  replacementTracks: SmartPlaylistTrack[];
  genomeUpdated: boolean;
}

export interface ScoreResult {
  success: boolean;
  overallScore: number;
  skipRate: number;
  completionRate: number;
}

export interface SmartPlaylistSummary {
  id: string;
  name: string;
  description: string | null;
  track_count: number;
  total_duration_seconds: number;
  overall_score: number | null;
  tags: string[];
  created_at: string;
  updated_at: string | null;
  last_played_at: string | null;
  play_count: number;
  was_modified: boolean;
  saved_to_library?: boolean;
}

export interface TasteGenome {
  energy_curve: Record<string, number>;
  genre_nuance: Record<string, number>;
  familiarity_preference: {
    comfort_ratio: number;
    deep_cut_affinity: number;
    classic_hit_fatigue: number;
  };
  artist_affinity: Record<string, any>;
  transition_preferences: any;
  context_patterns: Record<string, any>;
  correction_history: any[];
}

export interface GenomeScores {
  overall_accuracy: number;
  skip_rate_in_generated: number;
  completion_rate: number;
  correction_frequency: number;
  trend: string;
  playlists_scored: number;
}

export interface EllaReport {
  genomeVersion: number;
  scores: GenomeScores;
  recentPlaylists: any[];
}

export interface EditChatResult {
  success: boolean;
  ellaResponse: string;
  intent: {
    action: 'replace_range' | 'add_tracks' | 'remove_tracks' | 'reorder' | 'swap' | 'replace_specific';
    fromIndex?: number;
    toIndex?: number;
    trackIndices?: number[];
    insertAtIndex?: number;
    count?: number;
    style?: string;
    keepIndices?: number[];
    moves?: { from: number; to: number }[];
  };
}

export interface EditApplyResult {
  success: boolean;
  newTrackIds: string[];
  tracksChanged?: number;
  genomeLearned?: boolean;
  ellaMessage?: string;
  tracks: SmartPlaylistTrack[];
}

// ── API Calls ────────────────────────────────────────────────────────

export interface GenerateOptions {
  mode?: 'playlist' | 'radio';
  seedType?: string;
  seedValue?: string;
  tuningParams?: {
    vocalWeight?: number;
    energyLevel?: number;
    tempoFeel?: number;
    variety?: number;
    eraFlexibility?: number;
  };
  tweaks?: string[];
  timeAware?: boolean;
  weatherAware?: boolean;
  tzOffset?: number;
  weatherDescription?: string;
  weatherTemp?: number;
  weatherCondition?: string;
  locationCity?: string;
  locationRegion?: string;
  bedtimeHour?: number;
  timeOfDayLabel?: string;
  timeOfDayPeriod?: string;
  ambientMoodLabel?: string;
  ambientMoodSuggestion?: string;
  ambientEnergy?: number;
  ambientAcoustic?: number;
  ambientValence?: number;
  ambientVocal?: number;
  recentTrackIds?: string[];
  stationId?: string;
}

/** Blueprint + Qobuz resolution can exceed the default 25s API timeout. */
const PLAYLIST_BUILD_TIMEOUT_MS = 120_000;
const STAGE1_TIMEOUT_MS = 90_000;

function buildGenerateBody(transcript: string, options?: GenerateOptions) {
  return {
    transcript: options?.mode === 'radio' && options?.seedType ? undefined : transcript,
    mode: options?.mode,
    seedType: options?.seedType,
    seedValue: options?.seedValue,
    tuningParams: options?.tuningParams,
    currentTime: new Date().toISOString(),
    wasVoiceInput: true,
    ...(options || {}),
  };
}

function isStage1UnsupportedError(err: unknown): boolean {
  const msg = String((err as Error)?.message || err || '');
  return msg.includes('API 404') || msg.includes('API 405');
}

export interface ContinueOptions {
  playedTrackIds?: string[];
  skippedTrackIds?: string[];
  tuningParams?: GenerateOptions['tuningParams'];
  tweaks?: string[];
  timeAware?: boolean;
  weatherAware?: boolean;
  tzOffset?: number;
  weatherDescription?: string;
  weatherTemp?: number;
  weatherCondition?: string;
  locationCity?: string;
  locationRegion?: string;
  bedtimeHour?: number;
  timeOfDayLabel?: string;
  timeOfDayPeriod?: string;
  ambientMoodLabel?: string;
  ambientMoodSuggestion?: string;
  ambientEnergy?: number;
  ambientAcoustic?: number;
  ambientValence?: number;
  ambientVocal?: number;
  /** Live DJ moment — weather/time just shifted; next tracks play immediately after current song. */
  momentInterrupt?: boolean;
  momentReason?: 'weather_shift' | 'time_shift' | 'combined_shift';
  previousAmbientLabel?: string;
  newAmbientLabel?: string;
}

export interface ContinueResult {
  success: boolean;
  ellaResponse: string;
  trackCount: number;
  reserveTrackCount?: number;
  totalDurationSeconds: number;
  tracks: SmartPlaylistTrack[];
  reserveTracks?: SmartPlaylistTrack[];
  poolSlice?: CompactPoolTrack[];
}

export const smartPlaylistService = {
  /** Generate a smart playlist or radio station from natural language transcript (or radio seed) */
  generate: (transcript: string, options?: GenerateOptions): Promise<GenerateResult> =>
    api.post('/api/playlists/generate', buildGenerateBody(transcript, options), {
      timeoutMs: PLAYLIST_BUILD_TIMEOUT_MS,
    }),

  /**
   * Ella Radio stage-1 (fast opener). Returns raw JSON — caller parses tolerantly.
   * Returns null when the worker lacks the endpoint (404/405).
   */
  fetchStage1Raw: async (transcript: string, options?: GenerateOptions): Promise<unknown | null> => {
    try {
      return await api.post('/api/playlists/generate/stage1', buildGenerateBody(transcript, options), {
        timeoutMs: STAGE1_TIMEOUT_MS,
      });
    } catch (err) {
      if (isStage1UnsupportedError(err)) return null;
      throw err;
    }
  },

  /**
   * @deprecated Prefer fetchStage1Raw + parseStage1Response for tolerant parsing.
   */
  tryGenerateStage1: async (transcript: string, options?: GenerateOptions): Promise<Stage1GenerateResult | null> => {
    const data = await smartPlaylistService.fetchStage1Raw(transcript, options);
    if (data == null) return null;
    const { parseStage1Response } = await import('../radio/parseStage1Response');
    const parsed = parseStage1Response(data);
    if (parsed.kind !== 'ok' || parsed.path !== 'two_stage') return null;
    return parsed.res as Stage1GenerateResult;
  },

  /** Continue an existing playlist/radio station with more tracks */
  continue: (playlistId: string, options?: ContinueOptions): Promise<ContinueResult> =>
    api.post('/api/playlists/continue', {
      playlistId,
      ...(options || {}),
    }, { timeoutMs: PLAYLIST_BUILD_TIMEOUT_MS }),

  /** Send mid-stream correction for a playlist */
  modify: (
    playlistId: string,
    feedback: string,
    currentTrackIndex: number,
  ): Promise<ModifyResult> =>
    api.post('/api/playlists/modify', {
      playlistId,
      feedback,
      currentTrackIndex,
    }),

  /** Report track outcomes for scoring */
  score: (
    playlistId: string,
    trackOutcomes: {
      trackId: string;
      wasPlayed?: boolean;
      wasSkipped?: boolean;
      playDurationSeconds?: number;
      userReaction?: string;
    }[],
  ): Promise<ScoreResult> =>
    api.post('/api/playlists/score', { playlistId, trackOutcomes }),

  /** List all smart playlists for the current user */
  list: (): Promise<{ success: boolean; playlists: SmartPlaylistSummary[] }> =>
    api.get('/api/playlists/smart'),

  /** Get full smart playlist details */
  getDetail: (playlistId: string): Promise<{ success: boolean; playlist: any; tracks: any[] }> =>
    api.get(`/api/playlists/smart/${playlistId}`),

  /** Nudge a stalled stage-2 build (client telemetry companion — one-shot). */
  kickStationBuild: (playlistId: string): Promise<{ success: boolean; kicked?: boolean; skipped?: boolean }> =>
    api.post(`/api/playlists/smart/${playlistId}/kick-build`, {}),

  /** Update playlist fields (name, saved_to_library) */
  patch: (playlistId: string, fields: { name?: string; saved_to_library?: boolean }): Promise<{ success: boolean }> =>
    api.patch(`/api/playlists/smart/${playlistId}`, fields),

  /** Delete a smart playlist */
  delete: (playlistId: string): Promise<{ success: boolean }> =>
    api.delete(`/api/playlists/smart/${playlistId}`),

  /** Get the user's taste genome */
  getGenome: (): Promise<{ success: boolean; genome: TasteGenome; scores: GenomeScores; version: number }> =>
    api.get('/api/valet/genome'),

  /** Trigger genome recalculation */
  recalculateGenome: (): Promise<{ success: boolean; version: number }> =>
    api.post('/api/valet/genome/recalculate'),

  /** Fast conversational edit — returns Ella's response + structured intent */
  editChat: (
    playlistId: string,
    feedback: string,
    currentTracks: { position: number; title: string; artist: string }[],
  ): Promise<EditChatResult> =>
    api.post('/api/playlists/edit/chat', { playlistId, feedback, currentTracks }),

  /** Apply the edit intent — background track mutation + genome learning, returns full updated track list */
  editApply: (
    playlistId: string,
    intent: EditChatResult['intent'],
    currentTracks: { position: number; title: string; artist: string; trackId: string }[],
    rawFeedback?: string,
  ): Promise<EditApplyResult> =>
    api.post('/api/playlists/edit/apply', { playlistId, intent, currentTracks, rawFeedback }),

  /** Get Ella's self-assessment report */
  getEllaReport: (): Promise<{ success: boolean } & EllaReport> =>
    api.get('/api/valet/ella-report'),
};
